/**
 * ExtractionCard — inline confirm-card rendered under a Sales Agent assistant
 * message that has `pending_extraction`. Mirrors the Email-mode success card
 * but with an explicit Edit / Confirm / Dismiss step.
 */
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Sparkles, Edit2, CheckCircle, X, Loader2, UserPlus, Building2, ChevronRight, AlertTriangle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELD_DEFS = [
  { key: 'company_name',          label: 'Company',           required: true },
  { key: 'contact_name',          label: 'Contact' },
  { key: 'contact_email',         label: 'Email' },
  { key: 'contact_phone',         label: 'Phone' },
  { key: 'city',                  label: 'City' },
  { key: 'state',                 label: 'State' },
  { key: 'site_address',          label: 'Site Address' },
  { key: 'connected_load_kva',    label: 'Sanctioned Load',   suffix: 'kVA' },
  { key: 'critical_load_kw',      label: 'Critical Load',     suffix: 'kW' },
  { key: 'monthly_kwh',           label: 'Monthly Consumption', suffix: 'kWh' },
  { key: 'monthly_bill_inr',      label: 'Monthly Bill',      prefix: '₹' },
  { key: 'tariff',                label: 'Tariff' },
  { key: 'discom',                label: 'DISCOM' },
  { key: 'backup_hours_required', label: 'Backup Required',   suffix: 'h' },
  { key: 'dg_capacity_kva',       label: 'DG Capacity',       suffix: 'kVA' },
  { key: 'dg_count',              label: 'DG Count' },
  { key: 'budget_inr',            label: 'Budget',            prefix: '₹' },
];

export default function ExtractionCard({ messageId, pending, onResolved, onNavigate }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState(() => ({ ...(pending?.extracted || {}) }));
  const [created, setCreated] = useState(null);
  const [hidden, setHidden] = useState(false);

  if (!pending || hidden) return null;

  const existing = pending.existing_prospect;
  const hasCompany = (fields.company_name || '').trim().length > 0;

  const setField = (k, v) => setFields(prev => ({ ...prev, [k]: v }));

  const onConfirm = async () => {
    if (!hasCompany) { toast.error('Company name is required'); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const { data } = await axios.post(`${API}/sales-agent/confirm-create`, {
        message_id: messageId,
        extracted: fields,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.already_created) {
        toast.info('Prospect was already created');
      } else {
        toast.success(`Added "${fields.company_name}" to pipeline`);
      }
      setCreated(data.created_records);
      onResolved && onResolved(messageId, data.created_records);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create prospect');
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      await axios.post(`${API}/sales-agent/dismiss-extraction`, { message_id: messageId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHidden(true);
      onResolved && onResolved(messageId, null);
    } catch (e) {
      toast.error('Could not dismiss');
    } finally {
      setBusy(false);
    }
  };

  // ── Created-state card (success) ──
  if (created) {
    const p = created.prospect;
    const s = created.site;
    return (
      <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3" data-testid="extraction-card-success">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-900">Added to pipeline</span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
          <Row label="Prospect" value={p?.company_name} />
          <Row label="Status" value={p?.status} />
          {s && <Row label="Site" value={s.site_name} />}
          {s?.city && <Row label="City" value={s.city} />}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-emerald-200">
          {p && (
            <button onClick={() => onNavigate(`/prospects/${p.prospect_id}`)}
              className="flex items-center gap-1 text-xs text-emerald-800 hover:text-emerald-900 bg-emerald-100 px-3 py-1.5 rounded"
              data-testid="extraction-view-prospect">
              <UserPlus className="w-3 h-3" /> View Prospect <ChevronRight className="w-3 h-3" />
            </button>
          )}
          {s && (
            <button onClick={() => onNavigate(`/prospects/sites/${s.prospect_site_id}`)}
              className="flex items-center gap-1 text-xs text-emerald-800 hover:text-emerald-900 bg-emerald-100 px-3 py-1.5 rounded"
              data-testid="extraction-view-site">
              <Building2 className="w-3 h-3" /> View Site <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Confirm-card (pre-create) ──
  return (
    <div className="mt-3 bg-amber-50/50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="extraction-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">Prospect detected — confirm to add</span>
        </div>
        <button onClick={onDismiss} disabled={busy}
          className="text-amber-600 hover:text-amber-900 disabled:opacity-50" data-testid="extraction-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>

      {existing && (
        <div className="bg-amber-100 border border-amber-300 rounded p-2 flex items-start gap-2 text-[11px] text-amber-900" data-testid="extraction-duplicate-warning">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>
            A prospect with this company name already exists.{' '}
            <button onClick={() => onNavigate(`/prospects/${existing.prospect_id}`)} className="underline font-medium">
              View existing
            </button>{' '}
            — confirming will create a duplicate.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
        {FIELD_DEFS.map(({ key, label, prefix, suffix, required }) => {
          const val = fields[key];
          const showAlways = required || (val !== null && val !== undefined && val !== '');
          if (!editing && !showAlways) return null;
          return (
            <div key={key} className="text-[11px]">
              <label className="block text-amber-800 font-medium mb-0.5">
                {label}{required && <span className="text-red-500">*</span>}
              </label>
              {editing ? (
                <div className="relative">
                  {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{prefix}</span>}
                  <Input
                    value={val == null ? '' : String(val)}
                    onChange={e => setField(key, e.target.value)}
                    className={`h-7 text-xs font-mono ${prefix ? 'pl-5' : ''} ${suffix ? 'pr-10' : ''}`}
                    data-testid={`extraction-input-${key}`}
                  />
                  {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{suffix}</span>}
                </div>
              ) : (
                <p className="text-slate-900 font-mono">
                  {val == null || val === '' ? <span className="text-slate-400 italic">—</span> :
                    `${prefix || ''}${typeof val === 'number' ? val.toLocaleString('en-IN') : val}${suffix ? ' ' + suffix : ''}`}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-amber-200">
        <Button size="sm" variant="outline" onClick={() => setEditing(e => !e)} disabled={busy}
          className="h-7 text-[11px]" data-testid="extraction-edit-btn">
          <Edit2 className="w-3 h-3 mr-1" />
          {editing ? 'Done editing' : 'Edit fields'}
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={onDismiss} disabled={busy}
          className="h-7 text-[11px] text-slate-500" data-testid="extraction-dismiss-btn">
          Dismiss
        </Button>
        <Button size="sm" onClick={onConfirm} disabled={busy || !hasCompany}
          className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700" data-testid="extraction-confirm-btn">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
          Add to Pipeline
        </Button>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-emerald-700 text-[10px] uppercase tracking-wide w-16">{label}</span>
    <span className="text-slate-900 font-medium">{value || '—'}</span>
  </div>
);
